import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Clock3, HandHeart, MapPin, Repeat2, Gauge } from "lucide-react";
import { SITE, IMAGES, LOGO } from "@/lib/site";
import { Seo } from "@/components/Seo";
import { Reveal, KineticLines, Chapter } from "@/components/motion";
import { Ribbon } from "@/components/Ribbon";

const Hero = () => {
  return (
    <section className="relative overflow-hidden" data-testid="home-hero">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-12 sm:pt-16">
        <Reveal y={12}>
          <div className="mx-auto w-fit rounded-2xl border border-navy/10 bg-white p-3 shadow-[0_16px_50px_rgba(26,46,68,0.12)]" data-testid="hero-logo-banner">
            <img
              src={LOGO}
              alt="Porter New Orleans — professional organizer and personal concierge service"
              className="h-36 w-auto sm:h-44"
            />
          </div>
        </Reveal>
      </div>
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 pb-24 pt-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-10 lg:pb-32 lg:pt-16">
        <div>
          <Reveal delay={0.05} y={14}>
            <p className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.3em] text-gold-dark">
              <span className="h-px w-8 bg-gold" aria-hidden="true" />
              Professional Organizer &amp; Personal Concierge
            </p>
          </Reveal>
          <KineticLines
            className="mt-7 font-serif text-5xl sm:text-6xl lg:text-[5.4rem] leading-[0.98] tracking-tight text-navy"
            lines={["More time.", "Less clutter.", <em key="life" className="text-gold-dark">Life, organized.</em>]}
          />
          <Reveal delay={0.75}>
            <p className="mt-8 max-w-md text-base sm:text-lg leading-relaxed text-navy/70">
              Since {SITE.founded}, Porter New Orleans has been the trusted extra set of hands for busy
              Greater New Orleans households — bringing calm, order, and breathing room back to everyday life.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Link
                to="/contact"
                data-testid="hero-cta-button"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-bold tracking-wide text-cream transition-all duration-300 hover:bg-navy-light hover:-translate-y-0.5"
              >
                Get in Touch
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-button"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-navy/25 px-8 py-4 text-sm font-bold tracking-wide text-navy transition-all duration-300 hover:border-gold hover:text-gold-dark"
              >
                Explore Services
              </Link>
            </div>
          </Reveal>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="absolute -left-4 -top-4 h-full w-full rounded-t-[10rem] border border-gold/50" aria-hidden="true" />
          <div className="relative overflow-hidden rounded-t-[10rem] shadow-[0_24px_70px_rgba(26,46,68,0.18)]">
            <img
              src={IMAGES.workCloset}
              alt="A walk-in closet organized by Porter New Orleans in Uptown New Orleans"
              className="h-[420px] w-full object-cover object-center sm:h-[540px]"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 rounded-2xl border border-navy/10 bg-white/90 px-6 py-4 shadow-[0_12px_40px_rgba(26,46,68,0.12)] backdrop-blur-md">
            <p className="font-serif text-3xl text-navy">2016</p>
            <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-gold-dark">Founded in New Orleans</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const PRESS = [
  "FOX 8 NOLA",
  "NOLA Adore Magazine",
  "The Pearl New Orleans",
  "It's New Orleans Podcast",
  "The Deed · CNBC",
];

const AsSeenOn = () => (
  <section className="border-b border-navy/10 bg-white py-16" data-testid="as-seen-on">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <p className="text-center text-[11px] font-bold uppercase tracking-[0.32em] text-gold-dark">As Seen On</p>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-x-14 gap-y-6">
          {PRESS.map((name) => (
            <span
              key={name}
              className="font-serif text-xl sm:text-2xl tracking-wide text-navy/55 transition-colors duration-300 hover:text-navy"
            >
              {name}
            </span>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

const Intro = () => (
  <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-32" data-testid="home-intro">
    <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
      <Reveal>
        <Chapter number="01" label="A quieter kind of luxury" />
        <h2 className="mt-7 font-serif text-4xl sm:text-5xl leading-[1.08] text-navy">
          Some seasons of life simply come with <em className="text-gold-dark">too much to hold.</em>
        </h2>
      </Reveal>
      <Reveal delay={0.15} className="flex flex-col justify-end">
        <p className="text-base sm:text-lg leading-relaxed text-navy/70">
          Porter New Orleans is a professional organization and personal concierge service founded by
          Kristina Fortier — a New Orleans native who understands busy households firsthand. For over a
          decade, Kristina has helped clients across Greater New Orleans simplify everyday life through
          thoughtful organization and personalized concierge support.
        </p>
        <p className="mt-5 text-base sm:text-lg leading-relaxed text-navy/70">
          No apps to learn, no call centers, no scripts. Just one experienced, detail-minded professional
          who treats your home and your to-do list like her own.
        </p>
        <Link
          to="/about"
          data-testid="intro-about-link"
          className="group mt-7 inline-flex w-fit items-center gap-2 text-sm font-bold tracking-wide text-gold-dark"
        >
          The story behind Porter
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const services = [
  {
    title: "Professional Organization",
    image: IMAGES.workPantryLabeled,
    alt: "A pantry organized by Porter New Orleans with labeled baskets and bins",
    copy: "Closets, kitchens, pantries, playrooms, home offices — we turn overwhelming spaces into calm, functional rooms that are easy to keep that way.",
    testid: "service-card-organization",
  },
  {
    title: "Personal Concierge",
    image: IMAGES.workFridge,
    alt: "A client's refrigerator stocked and organized by Porter New Orleans before their arrival",
    copy: "Errands, projects, planning, and the everyday responsibilities that pile up — handled personally and efficiently, so your time goes where it matters most.",
    testid: "service-card-concierge",
  },
];

const ServicesOverview = () => (
  <section className="bg-white py-24 lg:py-32" data-testid="home-services">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <Chapter number="02" label="How we help" />
        <div className="mt-7 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <h2 className="max-w-xl font-serif text-4xl sm:text-5xl leading-[1.08] text-navy">
            Two services. One goal: <em className="text-gold-dark">a lighter day.</em>
          </h2>
          <Link
            to="/services"
            data-testid="services-overview-link"
            className="group inline-flex w-fit items-center gap-2 text-sm font-bold tracking-wide text-navy"
          >
            Explore all services
            <ArrowRight size={15} className="text-gold-dark transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </Reveal>
      <div className="mt-14 grid gap-8 lg:grid-cols-2">
        {services.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.12}>
            <Link
              to="/services"
              data-testid={s.testid}
              className="group block overflow-hidden rounded-3xl border border-navy/10 bg-cream transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(26,46,68,0.14)]"
            >
              <div className="overflow-hidden">
                <img
                  src={s.image}
                  alt={s.alt}
                  loading="lazy"
                  className="h-72 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.045] sm:h-80"
                />
              </div>
              <div className="p-8 sm:p-10">
                <h3 className="font-serif text-3xl text-navy transition-colors duration-300 group-hover:text-gold-dark">
                  {s.title}
                </h3>
                <p className="mt-4 leading-relaxed text-navy/70">{s.copy}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.18em] text-gold-dark">
                  Learn more <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const problems = [
  "The house feels cluttered, and you can't quite get ahead of it.",
  "The to-do list never seems to get shorter — only longer.",
  "There's an organization project you've been putting off for months.",
  "Work and family leave little time for everything else.",
  "You simply need another capable set of hands.",
];

const ProblemSolution = () => (
  <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-32" data-testid="home-problem-solution">
    <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
      <Reveal>
        <Chapter number="03" label="Sound familiar?" />
        <h2 className="mt-7 font-serif text-4xl sm:text-5xl leading-[1.08] text-navy">
          Busy isn&rsquo;t a failure. <em className="text-gold-dark">It&rsquo;s a full life.</em>
        </h2>
        <p className="mt-7 max-w-md leading-relaxed text-navy/70">
          Most of our clients don&rsquo;t need more discipline — they need more hours. Porter exists
          to give those hours back, without judgment and without fuss.
        </p>
        <div className="mt-10 rounded-3xl border border-gold/40 bg-white p-8">
          <p className="font-serif text-2xl italic leading-snug text-navy">
            &ldquo;This is exactly where Porter comes in.&rdquo;
          </p>
          <p className="mt-4 text-sm leading-relaxed text-navy/65">
            One conversation is all it takes to hand off what&rsquo;s weighing on you.
          </p>
          <Link
            to="/contact"
            data-testid="problem-cta-button"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-bold text-cream transition-all duration-300 hover:bg-navy-light hover:-translate-y-0.5"
          >
            How Can We Help? <ArrowRight size={15} />
          </Link>
        </div>
      </Reveal>
      <div className="flex flex-col justify-center">
        {problems.map((p, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="group flex items-start gap-6 border-b border-navy/10 py-7 transition-colors duration-300 first:border-t hover:bg-white/60">
              <span className="font-serif italic text-2xl text-gold">{String(i + 1).padStart(2, "0")}</span>
              <p className="pt-0.5 text-lg leading-relaxed text-navy/80">{p}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const RateBand = () => (
  <section className="bg-navy py-20 lg:py-24" data-testid="home-rate-band">
    <div className="mx-auto flex max-w-7xl flex-col items-start gap-10 px-5 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
      <Reveal>
        <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-gold">Simple, transparent rate</p>
        <p className="mt-5 font-serif text-5xl sm:text-6xl text-cream" data-testid="rate-display">
          $85<span className="text-2xl sm:text-3xl text-gold-light"> / hour</span>
        </p>
        <p className="mt-5 max-w-md leading-relaxed text-cream/70">
          Billed hourly, with every engagement beginning with a conversation
          about what you need — no packages to decode, no surprises.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <Link
          to="/contact"
          data-testid="rate-cta-button"
          className="inline-flex items-center gap-2 rounded-full bg-gold px-9 py-4 text-sm font-bold tracking-wide text-navy-dark transition-all duration-300 hover:bg-gold-light hover:-translate-y-0.5"
        >
          Start the conversation <ArrowRight size={15} />
        </Link>
      </Reveal>
    </div>
  </section>
);

const reasons = [
  { icon: HandHeart, title: "Truly personal", copy: "Every engagement is tailored to your household, your pace, and your preferences." },
  { icon: Clock3, title: "Since 2016", copy: "Over a decade of hands-on experience in Greater New Orleans homes." },
  { icon: Sparkles, title: "Detail-obsessed", copy: "The folded corner, the labeled shelf, the errand handled before you ask twice." },
  { icon: Gauge, title: "Efficient by nature", copy: "A former corporate executive's discipline, applied to your everyday life." },
  { icon: MapPin, title: "Local knowledge", copy: "New Orleans native. She knows the city, its vendors, and its rhythms." },
  { icon: Repeat2, title: "Repeat & referral", copy: "Porter grows the old-fashioned way — clients come back, and they tell their friends." },
];

const WhyPorter = () => (
  <section className="bg-white py-24 lg:py-32" data-testid="home-why-porter">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <Chapter number="04" label="Why Porter" />
        <h2 className="mt-7 max-w-2xl font-serif text-4xl sm:text-5xl leading-[1.08] text-navy">
          The help you&rsquo;d ask a friend for — <em className="text-gold-dark">with a professional&rsquo;s follow-through.</em>
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-x-12 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
        {reasons.map((r, i) => (
          <Reveal key={r.title} delay={(i % 3) * 0.1}>
            <div className="group">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/50 text-gold-dark transition-all duration-300 group-hover:bg-navy group-hover:text-gold-light group-hover:border-navy">
                <r.icon size={19} strokeWidth={1.8} />
              </div>
              <h3 className="mt-5 font-serif text-2xl text-navy">{r.title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-navy/65">{r.copy}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const MeetPorter = () => (
  <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-32" data-testid="home-meet-porter">
    <Reveal className="mx-auto max-w-3xl text-center">
      <div className="flex items-center justify-center gap-4" data-testid="chapter-05">
        <span className="font-serif italic text-gold text-xl leading-none">05</span>
        <span className="h-px w-10 bg-gold/60" aria-hidden="true" />
        <span className="uppercase text-[11px] tracking-[0.28em] font-bold text-navy/60">Meet Porter New Orleans</span>
      </div>
      <h2 className="mt-7 font-serif text-4xl sm:text-5xl leading-[1.08] text-navy">
        Founded by someone who knows busy — <em className="text-gold-dark">and knows the cure.</em>
      </h2>
      <p className="mt-7 leading-relaxed text-navy/70">
        Porter was founded in 2016 by Kristina Fortier — a New Orleans native, busy mom, and former
        corporate executive — on a simple truth: sometimes what you need most is an extra set of hands,
        wheels, or brains.
      </p>
      <p className="mt-5 leading-relaxed text-navy/70">
        When you hire Porter, you get an experienced professional with an eye for detail, a corporate-honed
        efficiency, and a genuine understanding of what a full household carries.
      </p>
      <Link
        to="/about"
        data-testid="meet-porter-button"
        className="group mt-9 inline-flex items-center gap-2 rounded-full border border-navy px-8 py-4 text-sm font-bold tracking-wide text-navy transition-all duration-300 hover:bg-navy hover:text-cream"
      >
        Meet Porter New Orleans
        <ArrowRight size={15} className="text-gold-dark transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </Reveal>
  </section>
);

const GalleryTeaser = () => (
  <section className="bg-white py-24 lg:py-28" data-testid="home-gallery-teaser">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <Reveal>
          <Chapter number="06" label="The feeling of order" />
          <h2 className="mt-7 font-serif text-4xl sm:text-5xl leading-[1.08] text-navy">
            Calm you can <em className="text-gold-dark">see.</em>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link
            to="/gallery"
            data-testid="gallery-teaser-link"
            className="group inline-flex w-fit items-center gap-2 text-sm font-bold tracking-wide text-navy"
          >
            View the gallery
            <ArrowRight size={15} className="text-gold-dark transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-12 grid gap-5 sm:grid-cols-3">
        {[
          { src: IMAGES.workKitchen, alt: "A renovated kitchen unpacked and organized by Porter New Orleans in Uptown" },
          { src: IMAGES.workBookshelf, alt: "A navy built-in bookshelf styled by Porter New Orleans in Covington" },
          { src: IMAGES.workGirlsCloset, alt: "A children's closet organized by Porter New Orleans on St. Charles Avenue" },
        ].map((img, i) => (
          <Reveal key={img.src} delay={i * 0.1}>
            <div className="group overflow-hidden rounded-3xl">
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
              />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Porter New Orleans | Professional Organizer & Personal Concierge in New Orleans"
        description="Professional organization and personal concierge services for busy Greater New Orleans households. Since 2016. $85/hour. Get in touch through our contact form."
        path="/"
      />
      <Hero />
      <Ribbon />
      <AsSeenOn />
      <Intro />
      <ServicesOverview />
      <ProblemSolution />
      <RateBand />
      <WhyPorter />
      <MeetPorter />
      <GalleryTeaser />
    </>
  );
}
